import { useContext } from "react";
import type { MouseEvent } from "react";

import cc from "classcat";
import { Button } from "@narmi/design_system";
import Account from "byzantine/src/Account";
import { useLocalization } from "@fluent/react";

import AccountContext from "../../../../components/contexts/AccountContext";
import { requestNavigation } from "../../../utils/navigationGuard";
import { MAX_ACCOUNTS_TO_LIST } from "../sidebar.constants";
import { useSidebarContainer } from "../sidebar.hooks";
import { isSubItemRouteActive } from "../sidebar.highlighting";
import usePathname from "../../../../components/hooks/usePathname";

import styles from "./AccountsMenuItem.module.scss";

type AccountsContext = {
  favoritedAccounts: Account[];
  unfavoritedAccounts: Account[];
  isUpdatingAccounts: boolean;
};

const AccountsMenuItem = () => {
  const { l10n } = useLocalization();
  const { isMenuGroupExpanded } = useSidebarContainer();
  const { favoritedAccounts, unfavoritedAccounts } = useContext(
    AccountContext
  ) as AccountsContext;
  const pathname = usePathname();

  const tabIndex = isMenuGroupExpanded("accounts") ? 0 : -1;

  const handleViewAllClick = (event: MouseEvent) => {
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    ) {
      return;
    }
    event.preventDefault();
    requestNavigation(() => window.location.assign("/"));
  };

  /**
   * Displays at most MAX_ACCOUNTS_TO_LIST accounts at a time. First display any and all favorites,
   * and then use non-favorite accounts until max is reached.
   */
  const accountsToRender = [...favoritedAccounts, ...unfavoritedAccounts].slice(
    0,
    MAX_ACCOUNTS_TO_LIST
  );
  const showViewAll =
    favoritedAccounts.length + unfavoritedAccounts.length >
    MAX_ACCOUNTS_TO_LIST;

  return (
    <>
      {accountsToRender.map(({ id, name, balances, product_type }) => {
        const goToAccountPage = () =>
          requestNavigation(() => window.location.assign(`/accounts/${id}`));
        const isAccountRowActive = isSubItemRouteActive(
          `/accounts/${id}`,
          pathname
        );
        const balance = [
          ...Account.PRODUCT_GROUPS.Checking,
          ...Account.PRODUCT_GROUPS.Savings,
        ].includes(product_type)
          ? balances.available
          : balances.ledger;
        const rowClass = cc([
          styles["account-row"],
          !isAccountRowActive && styles["account-row-hoverable"],
          isAccountRowActive && styles["account-row-active"],
        ]);

        return (
          <button
            className={rowClass}
            title={name}
            onClick={goToAccountPage}
            key={id}
            tabIndex={tabIndex}
            role="menuitem"
          >
            <span>{name}</span>
            <span
              className={
                isAccountRowActive ? undefined : styles["account-balance-text"]
              }
            >
              {balance}
            </span>
          </button>
        );
      })}
      {showViewAll && (
        <div className={styles["view-all-button"]}>
          <Button
            label={l10n.getString(
              "sidebar-accounts-section-view-all",
              {},
              "View all"
            )}
            kind="plain"
            as="a"
            href="/"
            onClick={handleViewAllClick}
            tabIndex={tabIndex}
          />
        </div>
      )}
    </>
  );
};

export default AccountsMenuItem;
